// ============================================================================
// California Notices Viewer
// Display and acknowledge all required California workplace notices
// ============================================================================

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Shield,
  Building2,
  Scale,
  Heart,
  DollarSign,
  Briefcase,
  ChevronRight,
  Eye,
  Info,
} from 'lucide-react';
import ESignature from './ESignature';

// ============================================================================
// CALIFORNIA NOTICES DATA
// ============================================================================

const CA_NOTICES = [
  {
    id: 'ca_sick_leave',
    title: 'California Paid Sick Leave',
    icon: Heart,
    category: 'Leave Rights',
    summary: 'Your rights to paid sick leave under California law',
    content: `CALIFORNIA PAID SICK LEAVE NOTICE

Under California law, you are entitled to paid sick leave:

ACCRUAL:
• You earn 1 hour of paid sick leave for every 30 hours worked
• Accrual begins on your first day of employment
• Your employer may cap accrual at 48 hours or 6 days

USAGE:
• You may use accrued sick leave beginning on the 90th day of employment
• Sick leave may be used for:
  - Your own health condition, diagnosis, care, or treatment
  - Care of a family member with a health condition
  - Victims of domestic violence, sexual assault, or stalking

FAMILY MEMBERS INCLUDE:
• Child (biological, adopted, foster, stepchild, legal ward)
• Parent (biological, adoptive, foster, stepparent, legal guardian)
• Spouse or registered domestic partner
• Grandparent, grandchild, sibling

PROTECTIONS:
• Your employer cannot retaliate against you for using or requesting sick leave
• You cannot be required to find a replacement worker as a condition of using sick leave

For more information, visit: www.dir.ca.gov/dlse/paid_sick_leave.htm`,
  },
  {
    id: 'ca_min_wage',
    title: 'California Minimum Wage',
    icon: DollarSign,
    category: 'Wage & Hour',
    summary: 'Current minimum wage requirements in California',
    content: `CALIFORNIA MINIMUM WAGE NOTICE

CURRENT MINIMUM WAGE (2026):
• California State Minimum Wage: $16.50 per hour (all employers)

LOCAL MINIMUM WAGE:
Many California cities and counties have higher minimum wage requirements. 
Check with your local jurisdiction for applicable rates.

Examples of cities with higher minimum wages:
• San Francisco
• Los Angeles
• San Jose
• Oakland
• Berkeley

OVERTIME:
• 1.5x regular rate for hours over 8 in a day
• 1.5x regular rate for hours over 40 in a week
• 2x regular rate for hours over 12 in a day
• 2x regular rate for all hours over 8 on the 7th consecutive day

EXEMPTIONS:
Certain employees may be exempt from minimum wage requirements based on:
• Job duties
• Salary level
• Professional, administrative, or executive status

For more information: www.dir.ca.gov/dlse/faq_minimumwage.htm`,
  },
  {
    id: 'ca_workers_comp',
    title: "Workers' Compensation",
    icon: Shield,
    category: 'Safety & Insurance',
    summary: 'Your rights if injured on the job',
    content: `WORKERS' COMPENSATION NOTICE

IF YOU ARE INJURED ON THE JOB:

1. REPORT THE INJURY
   • Report any work injury to your supervisor immediately
   • Request a workers' compensation claim form (DWC 1)

2. GET MEDICAL TREATMENT
   • You have the right to see a doctor
   • Emergency treatment is always available
   • Your employer may direct initial treatment for up to 30 days

3. YOUR RIGHTS:
   • Medical care paid by your employer's insurance
   • Temporary disability payments if you can't work
   • Permanent disability payments if you don't fully recover
   • Supplemental job displacement benefits
   • Death benefits for dependents

4. RETURN TO WORK
   • You have the right to return to work after recovery
   • Your employer must offer you your job back if available

5. NO RETALIATION
   • It is illegal for your employer to punish or fire you for:
     - Filing a workers' compensation claim
     - Testifying in another employee's case

WORKERS' COMPENSATION CARRIER:
[Your employer's workers' compensation information is provided separately]

For more information: www.dir.ca.gov/dwc`,
  },
  {
    id: 'ca_unemployment',
    title: 'Unemployment Insurance',
    icon: Briefcase,
    category: 'Insurance Benefits',
    summary: 'Information about unemployment benefits',
    content: `UNEMPLOYMENT INSURANCE (UI) NOTICE

You may be eligible for Unemployment Insurance benefits if you:
• Lose your job through no fault of your own
• Are able and available to work
• Are actively looking for work
• Have earned enough wages in your base period

HOW TO FILE:
• Online at: www.edd.ca.gov
• By phone: 1-800-300-5616

WHAT YOU NEED TO FILE:
• Social Security number
• Driver's license or ID card number
• Last employer's business name, address, and phone number
• Dates of employment
• Reason for separation

WEEKLY CERTIFICATION:
• You must certify for benefits every two weeks
• Report any work and earnings
• Continue looking for work

BENEFIT AMOUNT:
• Based on your past earnings
• Maximum weekly benefit amount varies by year

STATE DISABILITY INSURANCE (SDI):
You may also be eligible for SDI if you cannot work due to:
• Illness or injury not related to work
• Pregnancy or childbirth

For more information: www.edd.ca.gov`,
  },
  {
    id: 'ca_paid_family_leave',
    title: 'Paid Family Leave',
    icon: Heart,
    category: 'Leave Rights',
    summary: 'Paid leave to care for family members',
    content: `PAID FAMILY LEAVE (PFL) NOTICE

Paid Family Leave provides up to 8 weeks of partial wage replacement benefits when you need time off work to:

BOND WITH A NEW CHILD:
• Newborn
• Newly adopted child
• Newly placed foster child

CARE FOR A SERIOUSLY ILL FAMILY MEMBER:
• Child
• Parent
• Grandparent
• Grandchild
• Sibling
• Spouse or registered domestic partner
• Parent-in-law

MILITARY ASSIST:
• Participate in qualifying event related to a family member's military deployment

ELIGIBILITY:
• You must have earned at least $300 in wages during your base period
• You must be unable to do your regular work

BENEFIT AMOUNT:
• Approximately 60-70% of wages (depending on income)
• Up to maximum weekly benefit amount

HOW TO APPLY:
• Online at: www.edd.ca.gov
• After your first week of leave

JOB PROTECTION:
• PFL provides benefits only, not job protection
• Job protection may be available under FMLA or CFRA

For more information: www.edd.ca.gov/disability/paid-family-leave`,
  },
  {
    id: 'ca_dv_leave',
    title: 'Domestic Violence Leave',
    icon: Shield,
    category: 'Leave Rights',
    summary: 'Time off for victims of domestic violence',
    content: `VICTIMS OF DOMESTIC VIOLENCE, SEXUAL ASSAULT, AND STALKING

YOUR RIGHTS:

TIME OFF TO SEEK HELP:
You may take time off work to:
• Seek medical attention for injuries
• Obtain services from a shelter or crisis center
• Obtain psychological counseling
• Participate in safety planning
• Seek legal assistance
• Appear in court

REASONABLE ACCOMMODATIONS:
You may request reasonable accommodations for your safety, such as:
• Transfer
• Reassignment
• Modified schedule
• Changed work telephone
• Changed work station
• Lock installation
• Safety procedures

CONFIDENTIALITY:
• Your employer must keep your situation confidential
• Information may only be shared on a need-to-know basis

NO RETALIATION:
Your employer cannot discriminate or retaliate against you for:
• Being a victim
• Requesting time off or accommodations
• Testifying in legal proceedings

CERTIFICATION:
Your employer may request certification such as:
• Police report
• Court order
• Documentation from professional (medical, counselor, etc.)

For help: National Domestic Violence Hotline: 1-800-799-7233`,
  },
  {
    id: 'ca_emergency_contact',
    title: 'Emergency Information',
    icon: AlertTriangle,
    category: 'Safety',
    summary: 'Emergency contact and safety information',
    content: `EMERGENCY INFORMATION

IN CASE OF EMERGENCY:

EMERGENCY SERVICES: 911
• Fire
• Police
• Medical emergencies

OSHA - WORKPLACE SAFETY:
• California OSHA: 1-800-963-9424
• Report unsafe working conditions

LABOR COMMISSIONER:
• Wage claims: 1-866-924-9757
• Report labor law violations

EEOC - DISCRIMINATION:
• 1-800-669-4000
• Report workplace discrimination

WORKERS' COMPENSATION:
• 1-800-736-7401
• Report work injuries

YOUR EMPLOYER'S EMERGENCY CONTACTS:
• Building Emergency: [See posted notices]
• HR Emergency Line: [Contact HR]
• Manager on Duty: [Contact supervisor]

EVACUATION PROCEDURES:
• Know your exit routes
• Know your assembly point
• Follow instructions from emergency wardens

FIRST AID:
• Know the location of first aid kits
• Know who is trained in first aid/CPR
• Report all injuries to supervisor`,
  },
];

// ============================================================================
// NOTICE CARD COMPONENT
// ============================================================================

function NoticeCard({ notice, isAcknowledged, onView, onAcknowledge }) {
  const Icon = notice.icon;

  return (
    <Card className={`transition-all hover:shadow-md ${isAcknowledged ? 'border-green-200 bg-green-50' : ''}`}>
      <CardContent className="pt-5">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isAcknowledged ? 'bg-green-100' : 'bg-blue-100'
            }`}>
            {isAcknowledged ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Icon className="h-5 w-5 text-blue-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-900">{notice.title}</h3>
              <Badge variant="outline" className="text-xs">
                {notice.category}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mb-3">{notice.summary}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onView(notice)}>
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              {!isAcknowledged && (
                <Button size="sm" onClick={() => onAcknowledge(notice)}>
                  Acknowledge
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CaliforniaNoticesViewer({
  employee,
  existingSubmission,
  onComplete,
  onSave,
}) {
  const [acknowledgedNotices, setAcknowledgedNotices] = useState(
    existingSubmission?.acknowledgedNotices || []
  );
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [hasReadNotice, setHasReadNotice] = useState(false);

  const allAcknowledged = acknowledgedNotices.length === CA_NOTICES.length;

  // Handle view notice
  const handleViewNotice = (notice) => {
    setSelectedNotice(notice);
    setShowNoticeDialog(true);
    setHasReadNotice(false);
  };

  // Handle acknowledge single notice
  const handleAcknowledgeNotice = (notice) => {
    setSelectedNotice(notice);
    setShowNoticeDialog(true);
    setHasReadNotice(false);
  };

  // Handle scroll to bottom
  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasReadNotice(true);
    }
  };

  // Confirm acknowledge
  const confirmAcknowledge = () => {
    if (selectedNotice && !acknowledgedNotices.includes(selectedNotice.id)) {
      setAcknowledgedNotices([...acknowledgedNotices, selectedNotice.id]);
    }
    setShowNoticeDialog(false);
    setSelectedNotice(null);
  };

  // Final submit with signature
  const handleFinalSubmit = () => {
    if (allAcknowledged) {
      setShowSignatureDialog(true);
    }
  };

  // Handle signature
  const handleSigned = async (signatureData) => {
    const submissionData = {
      acknowledgedNotices,
      signature: signatureData,
      status: 'complete',
      submittedAt: new Date().toISOString(),
    };

    await onComplete?.(submissionData);
    setShowSignatureDialog(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            California Workplace Notices
          </h2>
          <p className="text-gray-600 mt-1">
            Required notices for California employees
          </p>
        </div>
        {allAcknowledged && (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            All Acknowledged
          </Badge>
        )}
      </div>

      {/* Progress */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Please review and acknowledge each notice. You have acknowledged{' '}
          <strong>{acknowledgedNotices.length}</strong> of{' '}
          <strong>{CA_NOTICES.length}</strong> required notices.
        </AlertDescription>
      </Alert>

      {/* Notices List */}
      <div className="grid gap-4">
        {CA_NOTICES.map((notice) => (
          <NoticeCard
            key={notice.id}
            notice={notice}
            isAcknowledged={acknowledgedNotices.includes(notice.id)}
            onView={handleViewNotice}
            onAcknowledge={handleAcknowledgeNotice}
          />
        ))}
      </div>

      {/* Final Submit */}
      {allAcknowledged && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-900">All Notices Acknowledged</h3>
                  <p className="text-sm text-green-700">Click below to sign and complete</p>
                </div>
              </div>
              <Button onClick={handleFinalSubmit} className="bg-green-600 hover:bg-green-700">
                Sign & Complete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notice Dialog */}
      <Dialog open={showNoticeDialog} onOpenChange={setShowNoticeDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNotice?.icon && <selectedNotice.icon className="h-5 w-5 text-blue-600" />}
              {selectedNotice?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedNotice?.category} • Please read carefully
            </DialogDescription>
          </DialogHeader>

          <ScrollArea
            className="h-[400px] border rounded-lg p-4 bg-gray-50"
            onScrollCapture={handleScroll}
          >
            <pre className="whitespace-pre-wrap text-sm font-sans text-gray-700">
              {selectedNotice?.content}
            </pre>
          </ScrollArea>

          {!hasReadNotice && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                Please scroll down to read the entire notice before acknowledging.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowNoticeDialog(false)}>
              Close
            </Button>
            {!acknowledgedNotices.includes(selectedNotice?.id) && (
              <Button
                onClick={confirmAcknowledge}
                disabled={!hasReadNotice}
                className={hasReadNotice ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I Have Read & Acknowledge
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ESignature
            documentTitle="California Workplace Notices"
            documentType="ca_notices"
            documentId={existingSubmission?.id || 'new'}
            signerId={employee?.id}
            signerName={`${employee?.profile?.firstName || employee?.firstName || ''} ${employee?.profile?.lastName || employee?.lastName || ''}`.trim()}
            signerEmail={employee?.email || ''}
            onSign={handleSigned}
            onCancel={() => setShowSignatureDialog(false)}
            consentText={`By signing below, I acknowledge that I have received, read, and understand all California workplace notices provided to me, including information about:

• Paid Sick Leave
• Minimum Wage
• Workers' Compensation
• Unemployment Insurance
• Paid Family Leave
• Domestic Violence Leave Rights
• Emergency Information

I understand these notices inform me of my rights and responsibilities as an employee in California.`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Named export for status card
export function CANoticesStatusCard({ submission, onView }) {
  const isComplete = submission?.status === 'complete';
  const acknowledgedCount = submission?.acknowledgedNotices?.length || 0;

  return (
    <Card className={`transition-all hover:shadow-md ${isComplete ? 'border-green-200' : ''}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-blue-100'
              }`}>
              <Building2 className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">California Notices</h4>
              <p className="text-sm text-gray-500">
                {isComplete
                  ? 'All notices acknowledged'
                  : `${acknowledgedCount}/${CA_NOTICES.length} acknowledged`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isComplete ? (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                In Progress
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={onView}>
              View
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
